// The Catalogue screen's server state (spec/14 §6, F-CAT-01, F-CAT-07), over the generated client —
// `packages/contracts/src/routes/hq-catalogue.ts`:
//
//   GET  /v1/hq/catalogue/search?q=          search the metadata provider (IGDB) for base games   hq:catalog
//   GET  /v1/hq/catalogue/games              every game mirrored so far, newest first             hq:catalog
//   POST /v1/hq/catalogue/games              mirror a base game into the catalogue by its igdbId   hq:catalog
//   POST /v1/hq/catalogue/games/{id}/refresh re-read a mirrored game from IGDB and re-cache its cover hq:catalog
//   GET  /v1/hq/catalogue/games/{id}/art-candidates?kind=  hero/logo candidates (SteamGridDB, IGDB)  hq:catalog
//   PUT/DELETE /v1/hq/catalogue/games/{id}/art/{kind}      pick one (copied to our CDN) / clear it   hq:catalog
//   …/games/{id}/trailers[/{trailerId}[/complete]]         trailer uploads (signed PUT)              hq:catalog
//   POST /v1/hq/catalogue/apps · PATCH …/apps/{id}         add / edit an app (spec/14 §6)            hq:catalog
//   POST /v1/hq/catalogue/apps/common                      add the common apps (idempotent)          hq:catalog
//   POST …/apps/{id}/icon[/{uploadId}/complete]            icon upload (signed PUT, `icon-upload.ts`) hq:catalog
//   PUT  /v1/hq/catalogue/games/{id}/detection             a title's detection rules (F-CAT-04)      hq:catalog
//   POST /v1/hq/catalogue/games/manual · PATCH …/games/{id} hand-create / edit a game (M3)           hq:catalog
//   GET  …/games/{id}/usage · POST …/games/{id}/(un)archive  venues offering it; archive (M3)         hq:catalog
//   POST /v1/hq/catalogue/detection/test                   test a pasted process list (F-HQ-16)      hq:catalog
//   GET  /v1/hq/catalogue/requests · POST …/{id}/resolve   title requests from venues (F-CAT-08)     hq:catalog
//
// All need `hq:catalog` and a second factor. Search, the mirror and the refresh call IGDB behind
// Twitch OAuth; without the provider configured they answer `503 catalogue-provider-unavailable`.
// Listing reads our own `game` table and needs no provider.
//
// The search text is not put in the address — the same reasoning as Players (`players/PlayerSearch.tsx`):
// a search is a provider call, not a linkable list, so it lives in the page's state.

import type {
  CatalogueRequest,
  CatalogueRequestList,
  CatalogueRequestStatus,
  CreateCatalogueGame,
  DetectionTestResult,
  ResolveCatalogueRequest,
  UpdateCatalogueGame,
  ArtCandidateList,
  ArtKind,
  CatalogueKind,
  CatalogueSearchResults,
  CreateCatalogueApp,
  Game,
  GameId,
  GameList,
  GameTrailer,
  GameTrailerId,
  PickGameArt,
  SeedCommonAppsResult,
  SetLaunchRecipes,
  TitleDetectionInput,
  UpdateCatalogueApp,
} from '@clavian/api-client';
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { useApi } from '../../api/client';

/** A page of the catalogue list: the api's maximum. */
const PAGE_SIZE = 100;

export const catalogueKeys = {
  all: ['hq', 'catalogue'] as const,
  search: (q: string) => ['hq', 'catalogue', 'search', q] as const,
  games: ['hq', 'catalogue', 'games'] as const,
  /** One kind's list — under `games`, so invalidating `games` refreshes both. */
  gamesOfKind: (kind: CatalogueKind) => ['hq', 'catalogue', 'games', kind] as const,
  art: (id: GameId, kind: ArtKind) => ['hq', 'catalogue', 'art', id, kind] as const,
  trailers: (id: GameId) => ['hq', 'catalogue', 'trailers', id] as const,
  usage: (id: GameId) => ['hq', 'catalogue', 'usage', id] as const,
  requests: (status: CatalogueRequestStatus | 'all') => ['hq', 'catalogue', 'requests', status] as const,
};

/**
 * The IGDB image-CDN URL for a cover reference (`coverImageId`, e.g. `co1rbo`): the **fallback** a
 * client draws when we hold no cached copy of our own. A mirrored game whose cover we have cached to
 * our object storage carries `coverUrl` (our media CDN, `PUBLIC_MEDIA_URL`) and uses that instead
 * (spec/14 §6); a search result — nothing mirrored yet — always uses this. `t_cover_big` is the ~2:3
 * portrait size the grid draws. This host must stay in step with the HQ console's `img-src`
 * (`IGDB_IMAGE_ORIGIN` in `pages/headers.ts`, added in #537), or the browser's content policy blocks
 * the search-preview covers.
 */
export function coverArtUrl(coverImageId: string): string {
  return `https://images.igdb.com/igdb/image/upload/t_cover_big/${coverImageId}.jpg`;
}

/**
 * The cover to draw for a mirrored game: our own cached copy on the media CDN (`coverUrl`) when we
 * hold one, else the IGDB reference, else `null` (a placeholder). Search results carry no `coverUrl`,
 * so they always fall back to IGDB.
 */
export function catalogueCoverSrc(game: Pick<Game, 'coverUrl' | 'coverImageId'>): string | null {
  if (game.coverUrl !== null && game.coverUrl !== '') return game.coverUrl;
  return game.coverImageId !== null && game.coverImageId !== '' ? coverArtUrl(game.coverImageId) : null;
}

/** The metadata provider's base games matching `q`, and whether the catalogue already holds each. */
export function useCatalogueSearch(q: string) {
  const api = useApi();
  return useQuery({
    queryKey: catalogueKeys.search(q),
    queryFn: (): Promise<CatalogueSearchResults> => api.searchCatalogue({ query: { q } }),
    enabled: q !== '',
    // A provider call, not our own data: don't re-run it when the tab regains focus, and don't spin
    // on a `503` — the provider being unconfigured is a deployment state, surfaced at once, not retried.
    refetchOnWindowFocus: false,
    retry: false,
  });
}

/** The catalogue's titles of one kind — mirrored games or HQ-added apps — newest first, from our own table. */
export function useCatalogueGames(kind: CatalogueKind = 'game') {
  const api = useApi();
  return useInfiniteQuery({
    queryKey: catalogueKeys.gamesOfKind(kind),
    queryFn: ({ pageParam }): Promise<GameList> =>
      api.listCatalogueGames({ query: { limit: PAGE_SIZE, kind, ...(pageParam === undefined ? {} : { cursor: pageParam }) } }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => (last.page.hasMore && last.page.cursor !== null ? last.page.cursor : undefined),
  });
}

/**
 * The one-click accept: mirror a base game by its `igdbId`. The catalogue dedupes on `igdbId`, so a
 * repeat returns the existing row — a retry is safe. On success the catalogue list is refetched so the
 * game appears under "In the catalogue".
 */
export function useMirrorGame() {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (igdbId: number) => api.mirrorCatalogueGame({ body: { igdbId } }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: catalogueKeys.games });
    },
  });
}

/**
 * Refresh one mirrored game from IGDB (F-CAT-07): re-read its metadata and re-upload its cover. On
 * success the catalogue list is refetched, so a changed cover or name appears. Idempotent — refreshing
 * an unchanged game rewrites the same record.
 */
export function useRefreshGame() {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: GameId): Promise<Game> => api.refreshCatalogueGame({ path: { id } }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: catalogueKeys.games });
    },
  });
}

/**
 * Set a game's launch recipes (F-CAT-05): how a station launches it. Replaces the whole set. On
 * success the catalogue list is refetched so the recipe count on the card updates.
 */
export function useSetLaunchRecipes() {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation<Game, unknown, { id: GameId; body: SetLaunchRecipes }>({
    mutationFn: ({ id, body }) => api.setCatalogueGameLaunchRecipes({ path: { id }, body }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: catalogueKeys.games });
    },
  });
}

/**
 * Set a title's detection rules (F-CAT-04, spec/10 §7.3): how a station recognises its process, so play is
 * tracked. Replaces the rules; on success the catalogue list is refetched.
 */
export function useSetTitleDetection() {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation<Game, unknown, { id: GameId; body: TitleDetectionInput }>({
    mutationFn: ({ id, body }) => api.setCatalogueTitleDetection({ path: { id }, body }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: catalogueKeys.games });
    },
  });
}

/**
 * The hero or logo candidates for a game (spec/14 §6, ADR-0028): SteamGridDB first, then IGDB (heroes only).
 * A provider call, like the search: no refetch on focus, no retry on a `503` (a source not configured).
 */
export function useArtCandidates(id: GameId, kind: ArtKind, enabled: boolean) {
  const api = useApi();
  return useQuery({
    queryKey: catalogueKeys.art(id, kind),
    queryFn: (): Promise<ArtCandidateList> => api.listGameArtCandidates({ path: { id }, query: { kind } }),
    enabled,
    refetchOnWindowFocus: false,
    retry: false,
  });
}

/** Pick a hero or logo: the server copies it to our media CDN and answers the updated game. */
export function useSetGameArt() {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation<Game, unknown, { id: GameId; kind: ArtKind; body: PickGameArt }>({
    mutationFn: ({ id, kind, body }) => api.setGameArt({ path: { id, kind }, body }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: catalogueKeys.games });
    },
  });
}

/** Clear a hero or logo (the job then leaves it empty; picking again restores one). */
export function useClearGameArt() {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation<Game, unknown, { id: GameId; kind: ArtKind }>({
    mutationFn: ({ id, kind }) => api.clearGameArt({ path: { id, kind } }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: catalogueKeys.games });
    },
  });
}

/** A game's trailers, newest first. */
export function useGameTrailers(id: GameId, enabled: boolean) {
  const api = useApi();
  return useQuery({
    queryKey: catalogueKeys.trailers(id),
    queryFn: async (): Promise<GameTrailer[]> => (await api.listGameTrailers({ path: { id } })).data,
    enabled,
  });
}

/** Remove a trailer (and its object). */
export function useDeleteTrailer() {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation<unknown, unknown, { id: GameId; trailerId: GameTrailerId }>({
    mutationFn: ({ id, trailerId }) => api.deleteGameTrailer({ path: { id, trailerId } }),
    onSuccess: (_result, { id }) => {
      void queryClient.invalidateQueries({ queryKey: catalogueKeys.trailers(id) });
    },
  });
}

/** Put a trailer in or out of the kiosk's lock-screen playlist (F-KIO-01, F-CAT-07). */
export function useSetTrailerInLoop() {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation<unknown, unknown, { id: GameId; trailerId: GameTrailerId; inKioskLoop: boolean }>({
    mutationFn: ({ id, trailerId, inKioskLoop }) => api.updateGameTrailer({ path: { id, trailerId }, body: { inKioskLoop } }),
    onSuccess: (_result, { id }) => {
      void queryClient.invalidateQueries({ queryKey: catalogueKeys.trailers(id) });
    },
  });
}

/** Add an app (spec/14 §6). A name another app has is `409 conflict`. */
export function useCreateApp() {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation<Game, unknown, CreateCatalogueApp>({
    mutationFn: (body) => api.createCatalogueApp({ body }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: catalogueKeys.games });
    },
  });
}

/** Edit an app — only the fields sent change. */
export function useUpdateApp() {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation<Game, unknown, { id: GameId; body: UpdateCatalogueApp }>({
    mutationFn: ({ id, body }) => api.updateCatalogueApp({ path: { id }, body }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: catalogueKeys.games });
    },
  });
}

/** "Add common apps": creates the ones the catalogue does not hold yet (idempotent). */
export function useSeedCommonApps() {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation<SeedCommonAppsResult, unknown, void>({
    mutationFn: () => api.seedCommonCatalogueApps(),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: catalogueKeys.games });
    },
  });
}

// ---------------------------------------------------------------------------------------------
// M3: hand-create, edit and archive a game; its usage; detection testing; title requests
// ---------------------------------------------------------------------------------------------

/** Create a game by hand (no IGDB record) — works with the provider down (F-CAT-07). */
export function useCreateGame() {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation<Game, unknown, CreateCatalogueGame>({
    mutationFn: (body) => api.createCatalogueGame({ body }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: catalogueKeys.games });
    },
  });
}

/** Edit a game — each metadata field sent is kept through refreshes (`editedFields`). */
export function useUpdateGame() {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation<Game, unknown, { id: GameId; body: UpdateCatalogueGame }>({
    mutationFn: ({ id, body }) => api.updateCatalogueGame({ path: { id }, body }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: catalogueKeys.games });
    },
  });
}

/** How many venues offer a title — what archiving it warns about. Read only when asked for (it is an audited read). */
export function useGameUsage(id: GameId, enabled: boolean) {
  const api = useApi();
  return useQuery({
    queryKey: catalogueKeys.usage(id),
    queryFn: () => api.getCatalogueGameUsage({ path: { id } }),
    enabled,
    refetchOnWindowFocus: false,
  });
}

/** Archive a title (hidden from venues adding titles; their entries keep working) or bring it back. */
export function useSetGameArchived() {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation<Game, unknown, { id: GameId; archived: boolean }>({
    mutationFn: ({ id, archived }) => (archived ? api.archiveCatalogueGame({ path: { id } }) : api.unarchiveCatalogueGame({ path: { id } })),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: catalogueKeys.games });
    },
  });
}

/** Test a pasted process list against every title's detection rules (F-HQ-16). Nothing is written. */
export function useDetectionTest() {
  const api = useApi();
  return useMutation<DetectionTestResult, unknown, string>({
    mutationFn: (processes) => api.testCatalogueDetection({ body: { processes } }),
  });
}

/** HQ's inbox of title requests (F-CAT-08), open first. */
export function useCatalogueRequests(status: CatalogueRequestStatus | 'all') {
  const api = useApi();
  return useQuery({
    queryKey: catalogueKeys.requests(status),
    queryFn: (): Promise<CatalogueRequestList> => api.listCatalogueRequests({ query: status === 'all' ? {} : { status } }),
  });
}

/** Answer a title request: added (naming the game) or dismissed. */
export function useResolveCatalogueRequest() {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation<unknown, unknown, { requestId: CatalogueRequest['id']; body: ResolveCatalogueRequest }>({
    mutationFn: ({ requestId, body }) => api.resolveCatalogueRequest({ path: { requestId }, body }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['hq', 'catalogue', 'requests'] });
    },
  });
}
